import { useState, useMemo } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { MFOItem } from "@/types/kanban";
import { CheckCircle2, Clock, AlertTriangle, CalendarDays } from "lucide-react";
import { enGB } from 'date-fns/locale';

interface MFOCalendarViewProps {
  onMFOClick?: (mfo: MFOItem) => void;
  mfoItems?: MFOItem[]; // Accept MFO items as a prop
}

const MFOCalendarView = ({ onMFOClick, mfoItems = [] }: MFOCalendarViewProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());

  // Use provided MFO items or empty array
  const mfosByDate = useMemo(() => {
    const result: Record<string, MFOItem[]> = {};

    mfoItems.forEach((mfo) => {
      if (mfo.dueDate) {
        const dateStr = mfo.dueDate.split('T')[0]; // Handle ISO format or just YYYY-MM-DD
        if (!result[dateStr]) {
          result[dateStr] = [];
        }
        result[dateStr].push(mfo);
      }
    });

    return result;
  }, [mfoItems]);

  const selectedDateMFOs = useMemo(() => {
    if (!selectedDate) return [];
    
    const dateStr = selectedDate.toISOString().split('T')[0];
    return mfosByDate[dateStr] || [];
  }, [selectedDate, mfosByDate]);

  // Day rendering function to show indicators for dates with MFOs
  const renderDay = (day: Date) => {
    const dateStr = day.toISOString().split('T')[0];
    const mfos = mfosByDate[dateStr] || [];
    
    if (mfos.length === 0) return null;
    
    // Count MFOs by status
    const counts = mfos.reduce((acc, mfo) => {
      acc[mfo.status] = (acc[mfo.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return (
      <div className="flex gap-0.5 mt-1 justify-center flex-wrap">
        {counts["not-started"] && (
          <span className="h-1.5 w-1.5 rounded-full bg-slate-400"></span>
        )}
        {counts["in-progress"] && (
          <span className="h-1.5 w-1.5 rounded-full bg-blue-500"></span>
        )}
        {counts["delayed"] && (
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500"></span>
        )}
        {counts["completed"] && (
          <span className="h-1.5 w-1.5 rounded-full bg-green-500"></span>
        )}
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-7">
        <div className="bg-card rounded-2xl border shadow-xl p-8 transition-all duration-300">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            locale={enGB}
            className="w-full"
            classNames={{
              months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
              month: "space-y-4 w-full",
              caption: "flex justify-center pt-1 relative items-center text-2xl font-bold text-primary",
              caption_label: "text-2xl font-bold",
              nav: "space-x-2 flex items-center",
              nav_button: "h-9 w-9 bg-background rounded-full p-0 opacity-70 hover:opacity-100 border border-border shadow-sm transition-all duration-200",
              nav_button_previous: "absolute left-2",
              nav_button_next: "absolute right-2",
              table: "w-full border-collapse space-y-1",
              head_row: "flex",
              head_cell: "text-muted-foreground rounded-md w-full font-semibold text-base tracking-wide py-2",
              row: "flex w-full mt-2",
              cell: "relative p-0 text-center text-base focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-xl last:[&:has([aria-selected])]:rounded-r-xl",
              day: "h-14 w-14 p-0 font-semibold rounded-xl transition-all duration-150 aria-selected:opacity-100 hover:bg-primary/10 hover:text-primary focus:bg-primary/20 focus:text-primary-foreground",
              day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground shadow-lg scale-105",
              day_today: "bg-accent text-accent-foreground border-2 border-primary scale-105",
              day_outside: "text-muted-foreground opacity-40",
              day_disabled: "text-muted-foreground opacity-30",
              day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
              day_hidden: "invisible",
            }}
            components={{
              DayContent: (props) => (
                <div className="h-full w-full flex flex-col items-center justify-center">
                  <span className="text-lg font-semibold">{props.date.getDate()}</span>
                  {renderDay(props.date)}
                </div>
              ),
            }}
          />
        </div>
      </div>
      
      <div className="lg:col-span-5">
        <div className="bg-card rounded-lg border shadow-sm p-6 space-y-4">
          <h3 className="font-semibold text-xl">
            {selectedDate?.toLocaleDateString("en-US", {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </h3>
          
          {selectedDateMFOs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <CalendarDays className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-center font-medium">No MFOs due on this date</p>
              <span className="text-xs mt-2">Try selecting another date or create a new MFO.</span>
            </div>
          ) : (
            <ul className="space-y-3">
              {selectedDateMFOs.map((mfo) => (
                <li 
                  key={mfo.id}
                  className="p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => onMFOClick && onMFOClick(mfo)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium text-base">{mfo.code}</span>
                    {mfo.status === "in-progress" && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        <Clock className="h-3 w-3 mr-1" />
                        In Progress
                      </Badge>
                    )}
                    {mfo.status === "not-started" && (
                      <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">
                        <Clock className="h-3 w-3 mr-1" />
                        Not Started
                      </Badge>
                    )}
                    {mfo.status === "completed" && (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Completed
                      </Badge>
                    )}
                    {mfo.status === "delayed" && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Delayed
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {mfo.title}
                  </p>
                  <div className="flex justify-between items-center">
                    <div className="text-xs text-muted-foreground">
                      Progress: {mfo.progress}%
                    </div>
                    <div className="w-24 bg-secondary h-1.5 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          mfo.progress < 30
                            ? "bg-red-500"
                            : mfo.progress < 70
                            ? "bg-amber-500"
                            : "bg-green-500"
                        }`}
                        style={{ width: `${mfo.progress}%` }}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default MFOCalendarView;
